import cidrRegex, { v6 } from 'cidr-regex';
import { Netmask } from 'netmask';
import { z } from 'zod';
import { stringToList } from '#screens/hosts/form/advanced/mappers/stringToList.ts';
import { isPrivate } from './helpers/ip-address.ts';

export const MIN_NETWORK_BITMASK = 8;
export const MAX_NETWORK_BITMASK = 30;

export const CIDR_LIST_FORMAT_ERR = 'Must be a comma-separated list of CIDR ranges';
export const CIDR_ERR = 'Please provide an IP range in CIDR notation (e.g. 192.168.2.0/24)';
export const NETWORK_RANGE_ERR = 'Must be a network address CIDR (e.g. 100.100.10.0/24, not 100.100.10.21/24)';
export const IPV6_NOT_SUPPORTED_ERR = 'IPv6 is not currently supported in routes';
export const BITMASK_RANGE_ERR = `Mask must be between /${MIN_NETWORK_BITMASK} and /${MAX_NETWORK_BITMASK}.`;
export const PUBLIC_NETWORK_ERR = 'Must be a private network range (RFC 1918) or carrier-grade NAT (RFC 6598).';

export const cidrListSchema = z
  .string()
  .refine((value) => {
    const valueList = stringToList(value);

    return valueList.every((cidr) => cidrRegex({ exact: true }).test(cidr));
  }, CIDR_LIST_FORMAT_ERR)
  .or(z.literal(''));

/**
 * This is similar to validateCustCidrRequired, but with somewhat different (more relaxed) rules.
 *
 *  - Does not enforce bitmask min/max
 *  - Does not require private range
 */
export const cidrRequiredSchema = z
  .string({
    required_error: CIDR_ERR,
  })
  .regex(cidrRegex({ exact: true }), CIDR_ERR)
  .superRefine((cidr, ctx) => {
    let netMask: Netmask;

    // Show a friendly error if the user tried an IPV6 address.
    if (v6({ exact: true }).test(cidr)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: IPV6_NOT_SUPPORTED_ERR,
        fatal: true,
      });

      return z.NEVER;
    }

    try {
      netMask = new Netmask(cidr);
    } catch {
      // If Netmask can't parse, something snuck through the regex somehow.
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: CIDR_ERR,
        fatal: true,
      });

      return z.NEVER;
    }

    // This should be safe, because we enforce strict CIDR format with regex above, which is what
    // toString() uses.
    if (netMask.toString() !== cidr) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: NETWORK_RANGE_ERR,
        fatal: true,
      });
      return z.NEVER;
    }

    return z.NEVER;
  });

/**
 * This schema is used when creating new Managed Nebula networks.  It requires a private-address space, and restricts the
 * bitmaps that can be used.
 */
export const privateCidrRequiredSchema = cidrRequiredSchema.superRefine((cidr, ctx) => {
  // Safe because we only allow valid cidr in cidrRequiredSchema
  const netMask = new Netmask(cidr);

  if (netMask.bitmask < MIN_NETWORK_BITMASK || netMask.bitmask > MAX_NETWORK_BITMASK) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: BITMASK_RANGE_ERR,
      fatal: true,
    });
    return z.NEVER;
  }

  if (!isPrivate(netMask)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: PUBLIC_NETWORK_ERR,
      fatal: true,
    });
    return z.NEVER;
  }

  return z.NEVER;
});
